/** SC-221/222/223 — buffet round restrictions, enforced at add-to-cart time.
 *
 *  The backend (enforce_dine_in_round) only rejects these at "send to kitchen",
 *  which let the customer build a cart that is refused at the very end. This pure
 *  helper computes the same gates up front so the add can be blocked immediately
 *  with a clear message. It is advisory only — the server remains authoritative.
 *
 *  Checks mirror the backend order: max rounds, then cooldown, then per-round
 *  item budget. The i18n keys are the same ones the checkout error path uses. */
export interface BuffetAddGateInputs {
  /** True only for a dine-in session whose restaurant is in buffet mode. */
  isBuffetDineIn: boolean;
  /** Items already in the current (unsent) round — local cart, or shared cart in shared mode. */
  roundItemCount: number;
  /** Effective max items per round (covers-scaled server-side). null ⇒ no budget gate. */
  roundItemBudget: number | null;
  /** Rounds already sent for this session (server's non-cancelled order count). */
  roundCount: number;
  /** Max rounds allowed for the session. null ⇒ unlimited (no rounds gate). */
  maxRounds: number | null;
  /** Seconds left before a new round may start. 0 ⇒ no cooldown active. */
  cooldownSecondsRemaining: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

/** Returns the toast message to show when an add must be blocked by a buffet
 *  round restriction, or null when the add is allowed. */
export function buffetAddBlockMessage(i: BuffetAddGateInputs): string | null {
  if (!i.isBuffetDineIn) return null;

  if (i.maxRounds != null && i.roundCount >= i.maxRounds) {
    return i.t('checkout.errors.roundsExhausted');
  }
  if (i.cooldownSecondsRemaining > 0) {
    return i.t('checkout.errors.roundCooldown', { seconds: i.cooldownSecondsRemaining });
  }
  if (i.roundItemBudget != null && i.roundItemCount >= i.roundItemBudget) {
    return i.t('checkout.errors.roundBudgetExceeded', { max: i.roundItemBudget });
  }
  return null;
}
